import type { MoneyDto } from "@/generated/api/types.gen"
import { DEFAULT_LOCALE, type Locale } from "@/i18n/locales"
import { currencyFormatter } from "@/lib/money/currency-format"

declare const brand: unique symbol

export type Currency = "MXN" | "USD"

export const DEFAULT_CURRENCY: Currency = "MXN"

export interface Money {
  readonly amount: string
  readonly currency: Currency
  readonly [brand]: "Money"
}

const SCALE = 5

const DECIMAL = /^-?\d+(\.\d{1,5})?$/

const CURRENCIES: readonly Currency[] = ["MXN", "USD"]

const isCurrency = (code: string): code is Currency => (CURRENCIES as readonly string[]).includes(code)

export const money = (amount: string, currency: Currency): Money => {
  if (!DECIMAL.test(amount)) throw new TypeError(`not a decimal amount: ${amount}`)
  return { amount, currency } as Money
}

export const moneyFromNumber = (value: number, currency: Currency): Money => money(String(Number(value.toFixed(SCALE))), currency)

export const moneyFromCents = (cents: number, currency: string): Money => {
  const code = currency.toUpperCase()
  if (!isCurrency(code)) throw new TypeError(`unsupported currency: ${currency}`)
  return money((Math.round(cents) / 100).toFixed(2), code)
}

export const moneyFromWire = (value: MoneyDto | null | undefined): Money | null => {
  if (!value) return null
  const currency = value.currency_code.toUpperCase()
  if (!isCurrency(currency) || !DECIMAL.test(value.amount)) return null
  return { amount: value.amount, currency } as Money
}

const toUnits = (value: Money): bigint => {
  const [whole = "0", fraction = ""] = value.amount.split(".")
  const units = BigInt(whole.replace("-", "")) * 10n ** BigInt(SCALE) + BigInt(fraction.padEnd(SCALE, "0"))
  return whole.startsWith("-") ? -units : units
}

const fromUnits = (units: bigint, currency: Currency): Money => {
  const magnitude = units < 0n ? -units : units
  const fraction = (magnitude % 10n ** BigInt(SCALE)).toString().padStart(SCALE, "0").replace(/0+$/, "")
  const sign = units < 0n ? "-" : ""
  return money(`${sign}${magnitude / 10n ** BigInt(SCALE)}${fraction ? `.${fraction}` : ""}`, currency)
}

const assertSameCurrency = (a: Money, b: Money) => {
  if (a.currency !== b.currency) throw new TypeError(`cannot combine ${a.currency} with ${b.currency}`)
}

export const addMoney = (a: Money, b: Money): Money => {
  assertSameCurrency(a, b)
  return fromUnits(toUnits(a) + toUnits(b), a.currency)
}

export const subtractMoney = (a: Money, b: Money): Money => {
  assertSameCurrency(a, b)
  return fromUnits(toUnits(a) - toUnits(b), a.currency)
}

export const multiplyMoney = (value: Money, quantity: number): Money => {
  if (!Number.isSafeInteger(quantity)) throw new TypeError("Money quantity must be a safe integer")
  return fromUnits(toUnits(value) * BigInt(quantity), value.currency)
}

export const compareMoney = (a: Money, b: Money): -1 | 0 | 1 => {
  assertSameCurrency(a, b)
  const [left, right] = [toUnits(a), toUnits(b)]
  return left === right ? 0 : left > right ? 1 : -1
}

export const isPositiveMoney = (value: Money): boolean => toUnits(value) > 0n

export const isNegativeMoney = (value: Money): boolean => toUnits(value) < 0n

export const moneyToNumber = (value: Money): number => Number(value.amount)

const formatterFor = (locale: Locale, currency: Currency, digits: number, currencyDisplay?: Intl.NumberFormatOptions["currencyDisplay"]): Intl.NumberFormat =>
  currencyFormatter({ locale, currency, minimumFractionDigits: digits, maximumFractionDigits: digits, currencyDisplay })

// Intl.NumberFormat v3 takes a decimal string and reads it at arbitrary precision;
// TypeScript still types the parameter as a numeric literal, so the cast is the only
// way to reach the behaviour that keeps this module free of floats.
const asDecimal = (amount: string) => amount as Intl.StringNumericLiteral

export const formatMoney = (value: Money, locale: Locale = DEFAULT_LOCALE): string => formatterFor(locale, value.currency, 2).format(asDecimal(value.amount))

export const formatMoneyRounded = (value: Money, locale: Locale = DEFAULT_LOCALE): string => formatterFor(locale, value.currency, 0).format(asDecimal(value.amount))

export const formatMoneyNarrow = (value: Money, locale: Locale = DEFAULT_LOCALE): string => formatterFor(locale, value.currency, 2, "narrowSymbol").format(asDecimal(value.amount))

export const formatMoneyWhole = (value: Money, locale: Locale = DEFAULT_LOCALE): string =>
  formatterFor(locale, value.currency, toUnits(value) % 10n ** BigInt(SCALE) === 0n ? 0 : 2, "narrowSymbol").format(asDecimal(value.amount))

export const formatMoneyWithCode = (value: Money, locale: Locale = DEFAULT_LOCALE): string => `${formatMoneyNarrow(value, locale)} ${value.currency}`
